// Department panel: open, close, and stay accessible while doing it.
//
// The mockup toggles a class and nothing else. A real storefront also has to trap
// focus, restore it on close, respond to Escape, and keep the page behind from
// scrolling -- none of which the mockup had to model.
class ApsNav extends HTMLElement {
  connectedCallback() {
    this.button = this.querySelector('[data-aps-panel-open]');
    this.panel = this.querySelector('.drawer');
    this.scrim = this.querySelector('[data-aps-panel-scrim]');
    this.closeBtn = this.querySelector('[data-aps-panel-close]');
    // The panel is required; the button is not. The desktop "All" button was removed on
    // 2026-09-08 and this guard used to demand it, so dropping the button would have silently
    // disabled the phone burger too: the burger asks on the document and this is what answers.
    if (!this.panel) return;

    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    this.button?.addEventListener('click', this.open);
    this.closeBtn?.addEventListener('click', this.close);
    this.scrim?.addEventListener('click', this.close);
    document.addEventListener('keydown', this.onKeydown);

    // The phone header carries its own burger, in a section that cannot reach this
    // one. It asks on the document; this answers. Marking the document tells the
    // header's CSS that the button has something to open -- without it the burger
    // stays invisible rather than becoming a control that does nothing.
    document.addEventListener('aps:panel-open', this.open);
    document.documentElement.setAttribute('data-aps-panel', 'ready');
  }

  disconnectedCallback() {
    document.removeEventListener('aps:panel-open', this.open);
    document.removeEventListener('keydown', this.onKeydown);
  }

  open(e) {
    // Whoever opened it is who focus goes back to. On a phone that is the header's
    // burger, in another section entirely; the desktop button is display:none there,
    // and focusing a hidden element silently drops focus on the floor.
    const asked = e?.detail?.trigger;
    this.opener = asked && asked.isConnected && asked.offsetParent !== null ? asked : this.button;
    // inert, not hidden. The mockup keeps the panel in the layout and slides it
    // off-canvas; `hidden` would drop it out of layout entirely and change its
    // measured geometry. inert keeps the geometry and still removes it from the
    // accessibility tree and the tab order while closed.
    this.panel.inert = false;
    if (this.scrim) this.scrim.inert = false;
    requestAnimationFrame(() => {
      this.panel.classList.add('on');
      this.scrim?.classList.add('on');
    });
    this.button.setAttribute('aria-expanded', 'true');
    document.body.style.overflow = 'hidden';
    (this.panel.querySelector('a, button') || this.panel).focus();
  }

  close() {
    if (!this.panel.classList.contains('on')) return;
    this.panel.classList.remove('on');
    this.scrim?.classList.remove('on');
    this.button.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';

    const finish = () => {
      this.panel.inert = true;
      if (this.scrim) this.scrim.inert = true;
    };
    // Wait for the slide-out before going inert, but never hang if the transition
    // was suppressed -- reduced-motion users get no transitionend at all.
    const t = setTimeout(finish, 400);
    this.panel.addEventListener('transitionend', function once(e) {
      if (e.target !== this || e.propertyName !== 'transform') return;
      clearTimeout(t);
      this.removeEventListener('transitionend', once);
      finish();
    });

    // Always the trigger, never "whatever was focused before". The panel is a
    // disclosure: the trigger is the element the user came from conceptually, and
    // it is guaranteed to still be in the document and focusable. Restoring
    // document.activeElement instead silently sends focus to <body> whenever the
    // panel was opened by anything other than a mouse click on the trigger.
    (this.opener || this.button)?.focus();
  }

  onKeydown(e) {
    if (!this.panel.classList.contains('on')) return;
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key !== 'Tab') return;

    const items = [...this.panel.querySelectorAll('a[href], button:not([disabled])')]
      .filter((el) => el.offsetParent !== null);
    if (items.length === 0) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}
customElements.define('aps-nav', ApsNav);

// ---------------------------------------------------------------- parts finder drawer
//
// The Parts Finder bar item is a real link to /collections/parts, so it works with no JavaScript.
// Here it is intercepted and opens the finder as a side drawer on the current page instead, which
// is what Dylan asked for: pick a machine without losing the page you were reading.
//
// A modified click (new tab, new window, middle button) is left alone. Someone cmd-clicking a nav
// link means to open the page, and swallowing that is the kind of thing that makes a nav feel
// broken for the people most likely to notice.
class ApsFinderDrawer {
  constructor(root) {
    this.root = root;
    this.drawer = root.querySelector('[id^="aps-finder-dw-"]');
    this.scrim = root.querySelector('[data-aps-finder-scrim]');
    this.openers = [...root.querySelectorAll('[data-aps-finder-open]')];
    if (!this.drawer || !this.scrim || !this.openers.length) return;

    this.onKey = this.onKey.bind(this);
    for (const a of this.openers) {
      a.addEventListener('click', (e) => {
        if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;
        e.preventDefault();
        this.open(a);
      });
    }
    this.scrim.addEventListener('click', () => this.close());
    this.drawer.querySelector('[data-aps-finder-close]')?.addEventListener('click', () => this.close());
  }

  open(opener) {
    this.opener = opener;
    this.drawer.removeAttribute('inert');
    this.scrim.removeAttribute('inert');
    this.drawer.classList.add('on');
    this.scrim.classList.add('on');
    for (const a of this.openers) a.setAttribute('aria-expanded', 'true');
    document.addEventListener('keydown', this.onKey);
    // The first select, not the drawer: the drawer is not focusable and the first thing anyone
    // wants here is step one.
    this.drawer.querySelector('select')?.focus();
  }

  close() {
    this.drawer.classList.remove('on');
    this.scrim.classList.remove('on');
    for (const a of this.openers) a.setAttribute('aria-expanded', 'false');
    document.removeEventListener('keydown', this.onKey);
    // inert only after the transition, or the panel disappears instead of sliding out.
    const done = () => { this.drawer.setAttribute('inert', ''); this.scrim.setAttribute('inert', ''); };
    const ms = parseFloat(getComputedStyle(this.drawer).transitionDuration) * 1000;
    if (ms > 0) this.drawer.addEventListener('transitionend', done, { once: true });
    else done();
    (this.opener || this.openers[0]).focus();
  }

  onKey(e) {
    if (e.key === 'Escape') { this.close(); return; }
    if (e.key !== 'Tab') return;
    const items = [...this.drawer.querySelectorAll('a[href], button:not([disabled]), select:not([disabled])')]
      .filter((el) => el.offsetParent !== null);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }
}

for (const root of document.querySelectorAll('.aps-nav-section, [id^="shopify-section-"]')) {
  if (root.querySelector('[data-aps-finder-open]')) { new ApsFinderDrawer(root); break; }
}
